import { createClient } from 'graphql-ws'
import { useQueryLoader } from 'react-relay'
import {
  Environment,
  Network,
  Observable,
  RecordSource,
  Store,
  type RequestParameters,
  type Variables
} from 'relay-runtime'

import { supabase } from '@/lib'

import {
  relayQueryNodes,
  type RelayPreloadedQueryByName,
  type RelayQueryMap,
  type RelayQueryName,
  type RelayQueryVariablesByName
} from './relayRegistry'

export type ScreenLoaderComponentProps<N extends RelayQueryName> = {
  queryRef: RelayPreloadedQueryByName<N>
}

const relayPreloadedQueriesMap: Map<string, RelayPreloadedQueryByName<RelayQueryName>> = new Map()

export const getRelayPreloadKey = <N extends RelayQueryName>(queryName: N, variables: RelayQueryVariablesByName<N>) => {
  const qs = Object.entries(variables)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${String(v)}`)
    .join('&')

  return `${queryName}${qs ? `?${qs}` : ''}`
}

export const saveRelayPreloadedQuery = <N extends RelayQueryName>(
  queryName: N,
  variables: RelayQueryVariablesByName<N>,
  preloadedQuery: RelayPreloadedQueryByName<N>
) => {
  const preloadKey = getRelayPreloadKey(queryName, variables)

  relayPreloadedQueriesMap.set(preloadKey, preloadedQuery)

  if (__DEV__) {
    console.debug(`[relay] saved query ${preloadKey}`, preloadedQuery)
  }
}

export const consumeRelayPreloadedQuery = <N extends RelayQueryName>(
  queryName: N,
  variables: RelayQueryVariablesByName<N>
) => {
  const preloadKey = getRelayPreloadKey(queryName, variables)
  const preloadedQuery = relayPreloadedQueriesMap.get(preloadKey) as RelayPreloadedQueryByName<N>

  if (preloadedQuery) {
    relayPreloadedQueriesMap.delete(preloadKey)

    if (__DEV__) {
      console.debug(`[relay] consumed query ${preloadKey}`, preloadedQuery)
    }

    return preloadedQuery
  }

  return undefined
}

export const useQueryLoaderByName = <N extends RelayQueryName>(
  queryName: N,
  initialQueryRef?: RelayPreloadedQueryByName<N>
) => useQueryLoader<RelayQueryMap[N]>(relayQueryNodes[queryName], initialQueryRef)

type RelayEnvironmentEntry = {
  environment: Environment
  dispose: () => void
}

const createRelayEnvironmentEntry = (): RelayEnvironmentEntry => {
  // create a new graphql-ws client, so we don't have stale tokens laying around after signout
  const client = createClient({
    url: process.env.EXPO_PUBLIC_GRAPHQL_URL,
    connectionParams: async () => {
      // always grab a fresh token!
      const { data } = await supabase.auth.getSession()

      return {
        authorization: `Bearer ${data?.session?.access_token || process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`
      }
    },
    // close sockets after 10 seconds inactivity
    lazyCloseTimeout: 10_000,
    // ping the server every 5 seconds to keep connections alive
    keepAlive: 5_000,
    // always retry connections in the case of an error or disconnection
    shouldRetry: () => true
  })
  const fetchOrSubscribe = (operation: RequestParameters, variables: Variables) =>
    Observable.create<any>((sink) =>
      operation.text
        ? client.subscribe(
            {
              operationName: operation.name,
              query: operation.text,
              variables
            },
            sink
          )
        : sink.error(new Error('Operation text cannot be empty'))
    )

  return {
    environment: new Environment({
      network: Network.create(fetchOrSubscribe, fetchOrSubscribe),
      store: new Store(new RecordSource())
    }),
    dispose: () => client.dispose()
  }
}

const relayEnvironmentListeners = new Set<() => void>()

let currentRelayEnvironmentEntry = createRelayEnvironmentEntry()

// must return a stable reference between swaps, otherwise useSyncExternalStore re-renders forever
export const getRelayEnvironment = () => currentRelayEnvironmentEntry.environment

export const subscribeToRelayEnvironment = (listener: () => void) => {
  relayEnvironmentListeners.add(listener)

  return () => void relayEnvironmentListeners.delete(listener)
}

export const initializeRelayEnvironment = () => {
  const previousEntry = currentRelayEnvironmentEntry

  relayPreloadedQueriesMap.clear()
  currentRelayEnvironmentEntry = createRelayEnvironmentEntry()
  // closes the previous session's socket, which would otherwise reconnect forever via shouldRetry
  previousEntry.dispose()
  relayEnvironmentListeners.forEach((listener) => listener())

  if (__DEV__) {
    console.debug('[relay] initialized environment')
  }
}
